import Navigation from "../components/Navigation";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const Strategie = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top immediately when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    if (location.pathname === "/") {
      // On home page, scroll to contact section
      const element = document.querySelector("#contact");
      if (element) {
        const navHeight = 80;
        const elementPosition = element.offsetTop - navHeight;
        window.scrollTo({
          top: elementPosition,
          behavior: "smooth",
        });
        window.history.pushState(null, null, "#contact");
      }
    } else {
      // On other pages, navigate to home page with contact section
      navigate("/#contact");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContactClick();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Skip link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-[#C5A66A] focus:text-[#0F1B2D] focus:px-4 focus:py-2 focus:rounded focus:font-semibold"
      >
        Skip to main content
      </a>

      <Navigation />
      <main id="main-content" className="pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-6xl">
          {/* Case Study */}
          <div className="mb-20">
            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl font-bold text-[#0F1B2D] mb-4">
                {t("strategie.title")}
              </h1>
              <h2 className="text-2xl font-semibold text-[#0F1B2D] mb-6">
                {t("strategie.subtitle")}
              </h2>
            </div>

            {/* Hero Results */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("strategie.heroResults.title")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("strategie.heroResults.results.gwpGrowth.value")}
                  </div>
                  <div className="text-gray-700">
                    {t("strategie.heroResults.results.gwpGrowth.description")}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("strategie.heroResults.results.ebitdaMargin.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "strategie.heroResults.results.ebitdaMargin.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("strategie.heroResults.results.marketingRoi.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "strategie.heroResults.results.marketingRoi.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("strategie.heroResults.results.salesCostRatio.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "strategie.heroResults.results.salesCostRatio.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("strategie.heroResults.results.newChannels.value")}
                  </div>
                  <div className="text-gray-700">
                    {t("strategie.heroResults.results.newChannels.description")}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("strategie.heroResults.results.leadersCertified.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "strategie.heroResults.results.leadersCertified.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm md:col-span-2 lg:col-span-3">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("strategie.heroResults.results.bestEmployer.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "strategie.heroResults.results.bestEmployer.description"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Circumstance */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("strategie.circumstance.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("strategie.circumstance.description")}
                </p>
              </div>
            </div>

            {/* Complication */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("strategie.complication.title")}
              </h3>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("strategie.complication.issues.fragmentedSales.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "strategie.complication.issues.fragmentedSales.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("strategie.complication.issues.outdatedBrand.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "strategie.complication.issues.outdatedBrand.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("strategie.complication.issues.marketingBudget.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "strategie.complication.issues.marketingBudget.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("strategie.complication.issues.leadershipSilos.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "strategie.complication.issues.leadershipSilos.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("strategie.complication.issues.limitedDigital.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "strategie.complication.issues.limitedDigital.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Catalyst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("strategie.catalyst.title")}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6">
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-3">
                      {t("strategie.catalyst.phases.phase1.title")}
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase1.items.northStar.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase1.items.northStar.description"
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase1.items.marketSizing.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase1.items.marketSizing.description"
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase1.items.brandRepositioning.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase1.items.brandRepositioning.description"
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase1.items.quickWins.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase1.items.quickWins.description"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-800 mb-3">
                      {t("strategie.catalyst.phases.phase2.title")}
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase2.items.omnichannel.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase2.items.omnichannel.description"
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase2.items.marketingTech.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase2.items.marketingTech.description"
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase2.items.leadershipProgram.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase2.items.leadershipProgram.description"
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase2.items.digitalDirect.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase2.items.digitalDirect.description"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-800 mb-3">
                      {t("strategie.catalyst.phases.phase3.title")}
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase3.items.performanceMarketing.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase3.items.performanceMarketing.description"
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase3.items.affinityPartnerships.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase3.items.affinityPartnerships.description"
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase3.items.advancedAnalytics.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase3.items.advancedAnalytics.description"
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase3.items.fieldSalesReorg.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase3.items.fieldSalesReorg.description"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-800 mb-3">
                      {t("strategie.catalyst.phases.phase4.title")}
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase4.items.internationalization.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase4.items.internationalization.description"
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase4.items.brandCommunities.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase4.items.brandCommunities.description"
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase4.items.okrTooling.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase4.items.okrTooling.description"
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                        <div>
                          <span className="font-semibold">
                            {t(
                              "strategie.catalyst.phases.phase4.items.maScout.title"
                            )}
                          </span>{" "}
                          {t(
                            "strategie.catalyst.phases.phase4.items.maScout.description"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Impact */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("strategie.impact.title")}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead className="bg-[#0F1B2D] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        {t("strategie.impact.table.headers.kpi")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("strategie.impact.table.headers.start")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("strategie.impact.table.headers.year7")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("strategie.impact.table.headers.delta")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("strategie.impact.table.rows.gwp.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("strategie.impact.table.rows.gwp.start")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("strategie.impact.table.rows.gwp.year7")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("strategie.impact.table.rows.gwp.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("strategie.impact.table.rows.ebitdaMargin.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("strategie.impact.table.rows.ebitdaMargin.start")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("strategie.impact.table.rows.ebitdaMargin.year7")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("strategie.impact.table.rows.ebitdaMargin.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("strategie.impact.table.rows.marketingRoi.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("strategie.impact.table.rows.marketingRoi.start")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("strategie.impact.table.rows.marketingRoi.year7")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("strategie.impact.table.rows.marketingRoi.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("strategie.impact.table.rows.digitalShare.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("strategie.impact.table.rows.digitalShare.start")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("strategie.impact.table.rows.digitalShare.year7")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("strategie.impact.table.rows.digitalShare.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("strategie.impact.table.rows.salesCostRatio.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("strategie.impact.table.rows.salesCostRatio.start")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("strategie.impact.table.rows.salesCostRatio.year7")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("strategie.impact.table.rows.salesCostRatio.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "strategie.impact.table.rows.leadershipEngagement.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "strategie.impact.table.rows.leadershipEngagement.start"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "strategie.impact.table.rows.leadershipEngagement.year7"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "strategie.impact.table.rows.leadershipEngagement.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "strategie.impact.table.rows.brandConsideration.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "strategie.impact.table.rows.brandConsideration.start"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "strategie.impact.table.rows.brandConsideration.year7"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "strategie.impact.table.rows.brandConsideration.delta"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Success Factors */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("strategie.successFactors.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "strategie.successFactors.factors.northStarMetrics.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "strategie.successFactors.factors.northStarMetrics.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "strategie.successFactors.factors.dataDrivenMarketing.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "strategie.successFactors.factors.dataDrivenMarketing.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "strategie.successFactors.factors.executiveGrowthCouncil.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "strategie.successFactors.factors.executiveGrowthCouncil.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "strategie.successFactors.factors.leadershipCoaching.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "strategie.successFactors.factors.leadershipCoaching.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t("strategie.successFactors.factors.ecosystemPlay.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "strategie.successFactors.factors.ecosystemPlay.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "strategie.successFactors.factors.maOrchestration.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "strategie.successFactors.factors.maOrchestration.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl text-indigo-600">"</div>
                  <div>
                    <p className="text-lg text-gray-700 italic mb-4">
                      {t("strategie.customerQuote.quote")}
                    </p>
                    <div className="font-semibold text-[#0F1B2D]">
                      {t("strategie.customerQuote.author")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 text-[#C5A66A]">
                {t("strategie.callToAction.title")}
              </h3>
              <p className="text-lg mb-6">
                {t("strategie.callToAction.description")}
              </p>
              <button
                onClick={handleContactClick}
                onKeyDown={handleKeyDown}
                className="bg-[#C5A66A] cursor-pointer text-[#0F1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
              >
                {t("strategie.callToAction.button")}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Strategie;
